/**
 * useStep1ItemDefinition — composes Step 1's full lifecycle.
 *
 * Three sub-flows, no upload (Story 3.W moved upload to Step 0):
 *
 *   - `useStructuredFetch` — fetches `GET /workflow/{tid}/item-definition/complete?format=structured`
 *     once Step 1's reducer status becomes draft/revising/approved.
 *     Surfaces the structured display payload to Step 1's body.
 *   - `useEditFlow` — generic inline-edit lifecycle bound to the
 *     structured response + `updateItemDefinition` for save.
 *   - Raw `result` + `importedChildTaras` — read directly off the
 *     reducer's Step 1 slot. `imported_child_taras` lives on the
 *     raw agent output (the backend's structured presenter strips
 *     it), so the hook unwraps it from `state.steps[1].result`.
 *
 * Returns `Step1BodyProps` ready to spread onto `<Step1ItemDefinition>`.
 *
 * This is the contract Stories 4-11 inherit: a per-step hook that
 * composes sub-flow hooks + returns a typed `StepNBodyProps`. The
 * orchestrator (WorkflowV2.tsx) becomes a thin dispatcher.
 */
import { useCallback } from 'react';
import { getItemDefinition, updateItemDefinition } from '../../../api/workflow';
import { useStructuredFetch } from '../hooks/useStructuredFetch';
import { useEditFlow } from '../hooks/useEditFlow';
import { useRefetchOnReviseComplete } from '../hooks/useRefetchOnReviseComplete';
import type { Step1BodyProps } from '../step-components/stepBodyProps';
import type { PerStepState } from '../state/workflowV2Types';

const HAS_OUTPUT_STATUSES = new Set(['draft', 'revising', 'approved']);

export interface UseStep1ItemDefinitionInput {
  threadId: string | null | undefined;
  stepState: PerStepState | undefined;
  /** From the reducer: true once every agent-backed step is approved. */
  workflowCompleted: boolean;
}

interface ItemDefinitionResponse {
  thread_id: string;
  format: string;
  item_definition?: unknown;
}

export function useStep1ItemDefinition({
  threadId,
  stepState,
  workflowCompleted,
}: UseStep1ItemDefinitionInput): Step1BodyProps {
  const status = stepState?.status ?? 'empty';
  const hasOutput = HAS_OUTPUT_STATUSES.has(status);

  // Same rule as Steps 2-8 (and the backend's _check_edit_permission):
  // a step is editable while it is the current draft, or once the whole
  // workflow is complete. Mid-run edits to an already-approved Step 1
  // would 403 on save — disable the Edit button instead.
  const editable = workflowCompleted || status === 'draft';

  // Structured fetch — fires when Step 1 has output and threadId is
  // known. Caller-supplied refetch triggers a re-fetch after save.
  // The axios client has a response interceptor that unwraps `.data`,
  // so `getItemDefinition` actually resolves to the response body —
  // we cast through `unknown` because `api/workflow.js` is still JS
  // and exposes the raw AxiosResponse type to TS consumers.
  const fetcher = useCallback(
    (_signal: AbortSignal): Promise<ItemDefinitionResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getItemDefinition(threadId) as unknown as Promise<ItemDefinitionResponse>;
    },
    [threadId],
  );

  const extract = useCallback((raw: ItemDefinitionResponse | null): unknown => {
    return raw?.item_definition ?? null;
  }, []);

  const structured = useStructuredFetch<ItemDefinitionResponse | null, unknown>({
    fetcher,
    extract,
    enabled: hasOutput && !!threadId,
  });

  // Refetch the structured item-def whenever the agent re-completes
  // after a revise (status: revising → draft|approved). Without this
  // the form keeps showing pre-revise data even though the reducer
  // slot has been updated by polling.
  useRefetchOnReviseComplete(status, structured.refetch);

  // Edit flow — generic over the structured shape. Save fires
  // `PUT /workflow/{tid}/item-definition/update` then re-fetches.
  const onSave = useCallback(
    async (draft: unknown) => {
      if (!threadId) return;
      await updateItemDefinition(threadId, draft);
      structured.refetch();
    },
    [threadId, structured],
  );

  const edit = useEditFlow<unknown>({
    source: structured.data,
    onSave,
  });

  // Raw imported_child_taras (hierarchical-mode only). Backend's
  // structured presenter doesn't surface this field, so we read it
  // from the raw agent output stored on the reducer's Step-1 slot.
  const rawResult: any = stepState?.result;
  const importedChildTaras: string[] | undefined =
    rawResult?.imported_child_taras ?? rawResult?.result?.imported_child_taras;

  return {
    status,
    errorMessage: stepState?.errorMessage ?? null,
    result: rawResult,
    importedChildTaras,
    editable,
    edit,
    structured,
  };
}
